import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { Description, Title, Link, OutlineButton } from './styled-components';
import { ReactComponent as VideoSvg } from '../../../assets/icons/video.svg';

interface ProtocolDrug {
  name: string;
  url: string;
}

interface ProtocolDrugsList {
  [key: string]: ProtocolDrug;
}

const PROTOCOL_DRUGS = gql`
  query protocolDrugs {
    protocolDrugs
  }
`;

const MEDICATIONS: ProtocolDrugsList = Object.freeze({
  Cetrotide: {
    name: 'Cetrotide',
    url: 'http://freedommedteach.com/eng/videos.html?play=cetrotide'
  },
  Follistim: {
    name: 'Follistim',
    url: 'https://portal.kindbody.com/medication-instruction?med_index=0&medication_name=Follistim'
  },
  Ganirelix: {
    name: 'Ganirelix',
    url: 'https://portal.kindbody.com/medication-instruction?med_index=1&medication_name=Ganirelix'
  },
  'Gonal-f': {
    name: 'Gonal-f',
    url: 'https://portal.kindbody.com/medication-instruction?med_index=2&medication_name=Gonal-f'
  },
  'hCG Trigger IM': {
    name: 'hCG Trigger IM',
    url: 'http://freedommedteach.com/eng/videos.html?play=novarel-hcg_draw_up%2Cgeneral_im'
  },
  'hCG Trigger SQ': {
    name: 'hCG Trigger SQ',
    url: 'http://freedommedteach.com/eng/videos.html?play=general_sq'
  },
  'Lupron Down Regulation': {
    name: 'Lupron Down Regulation',
    url: 'http://freedommedteach.com/eng/videos.html?play=leuprolide_acetate'
  },
  'Lupron Trigger': {
    name: 'Lupron Trigger',
    url: 'http://freedommedteach.com/eng/videos.html?play=leuprolide_trigger'
  },
  Menopur: {
    name: 'Menopur',
    url: 'https://portal.kindbody.com/medication-instruction?med_index=3&medication_name=Menopur'
  },
  'Microdose Lupron': {
    name: 'Microdose Lupron',
    url: 'http://freedommedteach.com/eng/videos.html?play=leuprolide_acetate'
  },
  'Microflare Lupron': {
    name: 'Microflare Lupron',
    url: 'http://freedommedteach.com/eng/videos.html?play=leuprolide_acetate'
  },
  'Ovidrel Trigger': {
    name: 'Ovidrel',
    url: 'http://www.freedommedteach.com/eng/videos.html?play=ovidrel'
  },
  'Progesterone in Oil': {
    name: 'Progesterone in Oil',
    url: 'http://freedommedteach.com/eng/videos.html?play=progesterone_in_oil'
  }
});

const LearnAboutInjections = () => {
  const [protocolDrugs, setProtocolDrugs] = useState([]);

  useQuery(PROTOCOL_DRUGS, {
    onCompleted: data => setProtocolDrugs(data.protocolDrugs)
  });

  return (
    <>
      <Title>Learn about injections</Title>
      <Description>
        The videos below are for the specific drugs your physician has selected for you. It&apos;s
        critical that you watch each one carefully before your initial cycle visit because you
        can&apos;t start cycling without knowing exactly how the injections work. Each day, your
        care team will display your medication instructions on the dashboard.
      </Description>
      <Description>
        Still not sure? Reach out to{' '}
        <a href="mailto:navigator@kindbody.com">navigator@kindbody.com</a>.
      </Description>

      <Description>
        {protocolDrugs.map(drugName => {
          const medication = MEDICATIONS[drugName];
          if (!medication) {
            return null; // not all drugs have videos
          }
          return (
            <SmallOutlineButton key={medication.name}>
              <Link href={medication.url} target="_blank">
                <VideoSvg width={18} />
                &nbsp; {medication.name}
              </Link>
            </SmallOutlineButton>
          );
        })}
      </Description>
    </>
  );
};

export default LearnAboutInjections;

const SmallOutlineButton = styled(OutlineButton)`
  line-height: 0;
  margin-right: 24px;
  padding: 5px 10px;

  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    font-size: inherit;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    margin-top: 10px;
  }
`;
